import { formatAmount } from "../../../utils";
import { LoanApplication, LOSPlatform } from "../../../contexts";
import { FC, useMemo } from "react";
import {
  LoanApplicationCaseStatus,
  LoanApplicationCaseStatusForCreditLineDashboardMappingToText,
  LoanApplicationStatus,
  LoanApplicationStatusDashboardMappingToText,
} from "../../../hooks/api/types";
import { isPostActiveStatus } from "../LineOfCredit/service";
import { LoanDashboardProgress } from "../LoanDashboardProgress";

type LoanDetailsCreditLineProps = {
  loan: LoanApplication;
};

export const LoanDetailsCreditLine: FC<LoanDetailsCreditLineProps> = ({
  loan,
}) => {
  const {
    totalAmount,
    availableAmount,
    outstandingAmount,
    availabilityPercentage,
    tenureValue,
    lateFee,
  } = useMemo(() => {
    const totalAmount =
      loan?.approvedAmount ||
      loan?.creditLine?.approvedAmount ||
      loan.creditLine?.requestedLimit ||
      0;
    const availableAmount =
      loan?.availableAmount || loan?.creditLine?.availableAmount || 0;
    const outstandingAmount =
      loan?.remainingAmount || loan?.creditLine?.outstandingAmount || 0;
    const decision = loan?.priorityDecision;
    const tenureValue = decision
      ? decision?.tenure && decision?.tenureUnit
        ? `${decision.tenure} ${decision.tenureUnit}`
        : "-"
      : loan.creditLine?.duration && loan.creditLine?.durationType
      ? `${loan.creditLine.duration} ${loan.creditLine.durationType}`
      : "-";
    const lateFee = loan?.creditLine?.lateFee ?? "-";
    const availabilityPercentage =
      loan.creditLine?.availableAmount != null
        ? Math.round((loan.creditLine?.availableAmount / totalAmount) * 100)
        : totalAmount > 0
        ? Math.round((availableAmount / totalAmount) * 100)
        : 0;

    return {
      totalAmount,
      availableAmount,
      outstandingAmount,
      availabilityPercentage,
      tenureValue,
      lateFee,
    };
  }, [loan]);

  const showProgress = useMemo(() => {
    return (
      loan.status === LoanApplicationStatus.ACTIVE ||
      loan.status === LoanApplicationStatus.WAITING_FOR_DISBURSEMENT ||
      isPostActiveStatus(loan.caseStatus)
    );
  }, [loan]);

  const isActiveStatus = useMemo(() => {
    return loan.losPlatform === LOSPlatform.CREDIBLEX
      ? isPostActiveStatus(loan.caseStatus)
      : loan.status === LoanApplicationStatus.ACTIVE;
  }, [loan.status, loan.caseStatus, loan.losPlatform]);

  const renderFacilityLabel = () => {
    if (loan.losPlatform === LOSPlatform.CREDIBLEX) {
      return (
        LoanApplicationCaseStatusForCreditLineDashboardMappingToText[
          loan.caseStatus
        ] || "Requested Facility"
      );
    } else {
      return (
        LoanApplicationStatusDashboardMappingToText[loan.status] ||
        "Requested Facility"
      );
    }
  };

  return (
    <div className="sm:cx-flex cx-flex-wrap cx-items-center cx-py-4 cx-px-2 sm:cx-divide-x sm:cx-divide-neutral-light">
      <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
        <div className="cx-text-[#666] cx-text-sm cx-font-medium">
          {renderFacilityLabel()}
        </div>
        <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
          <div className="cx-text-text-primary cx-font-medium cx-text-xl">
            <div className="cx-font-aed-font"> D </div>
          </div>
          <div className="cx-text-text-primary cx-font-bold cx-text-xl">
            {formatAmount(totalAmount)}
          </div>
        </div>
      </div>

      {isActiveStatus ? (
        <>
          <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
            <div className="cx-text-[#666] cx-text-sm cx-font-medium">
              Available amount
            </div>
            <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
              <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                <div className="cx-font-aed-font"> D </div>
              </div>
              <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                {formatAmount(availableAmount)}
              </div>
            </div>
          </div>

          <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
            <div className="cx-text-[#666] cx-text-sm cx-font-medium">
              Outstanding amount
            </div>
            <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
              <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                <div className="cx-font-aed-font"> D </div>
              </div>
              <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                {formatAmount(outstandingAmount)}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
          <div className="cx-text-[#666] cx-text-sm cx-font-medium">
            Duration
          </div>
          <div className="cx-grow cx-flex cx-items-center cx-justify-center">
            <div className="cx-text-text-primary cx-font-bold cx-text-xl">
              {tenureValue ?? "N/A"}
            </div>
          </div>
        </div>
      )}
      {showProgress &&
        lateFee != null &&
        loan.caseStatus == LoanApplicationCaseStatus.PAST_MATURITY && (
          <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
            <div className="cx-text-text-secondary cx-text-sm cx-font-medium">
              Late fee
            </div>
            <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
              <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                <div className="cx-font-aed-font"> D </div>
              </div>
              <div className="cx-font-bold cx-text-xl">
                {lateFee != "-" ? formatAmount(lateFee as number) : "-"}
              </div>
            </div>
          </div>
        )}
      {showProgress && (
        <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-justify-center cx-py-3 cx-px-4">
          <LoanDashboardProgress
            value={availabilityPercentage}
            label="Available"
            invertFill
          />
        </div>
      )}
    </div>
  );
};

export default LoanDetailsCreditLine;
